import { useQuery } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import {
  DashboardApi,
  type DashboardApiDashboardControllerGetCurrentTasksByPhaseRequest,
} from "@/config/apiClient/api";
import { createApiClient } from "@/config/createApiClient";
import { currentWorkspaceAtom } from "@/store";
import { useRolePermission } from "../rolePermission/useRolePermission";

const apiClient = createApiClient(DashboardApi);

export const useGetCurrentTasksByPhase = ({
  phase,
  limit = 10,
  offset = 0,
}: {
  phase?: DashboardApiDashboardControllerGetCurrentTasksByPhaseRequest["phase"];
  limit?: number;
  offset?: number;
}) => {
  const currentWorkspace = useAtomValue(currentWorkspaceAtom);

  return useQuery({
    queryKey: [
      "dashboard",
      "current-tasks",
      "by-phase",
      phase,
      limit,
      offset,
      currentWorkspace?.accountId,
      currentWorkspace?.organizationId,
      currentWorkspace?.id,
    ],
    enabled: !!currentWorkspace,
    queryFn: () =>
      apiClient.dashboardControllerGetCurrentTasksByPhase({
        accountId: currentWorkspace?.accountId,
        organizationId: currentWorkspace?.organizationId,
        workspaceId: currentWorkspace?.id,
        phase,
        limit,
        offset,
      }),
  });
};

export const useGetDeliveredTasks = ({ limit = 10, offset = 0 }) => {
  const currentWorkspace = useAtomValue(currentWorkspaceAtom);

  const { hasAdvertiserKazaarRole } = useRolePermission();

  return useQuery({
    queryKey: [
      "dashboard",
      "delivered-tasks",
      limit,
      offset,
      currentWorkspace?.accountId,
      currentWorkspace?.organizationId,
      currentWorkspace?.id,
    ],
    enabled: hasAdvertiserKazaarRole && !!currentWorkspace,
    queryFn: () =>
      apiClient.dashboardControllerGetDeliveredTasks({
        accountId: currentWorkspace?.accountId,
        organizationId: currentWorkspace?.organizationId,
        workspaceId: currentWorkspace?.id,
        limit,
        offset,
      }),
  });
};

export const useGetNewRequests = ({ limit = 10, offset = 0 }) => {
  const currentWorkspace = useAtomValue(currentWorkspaceAtom);

  const { hasBrokerKazaarRole } = useRolePermission();

  return useQuery({
    queryKey: [
      "dashboard",
      "orders",
      "without-csm",
      limit,
      offset,
      currentWorkspace?.accountId,
      currentWorkspace?.organizationId,
      currentWorkspace?.id,
    ],
    enabled: hasBrokerKazaarRole && !!currentWorkspace,
    queryFn: () =>
      apiClient.dashboardControllerGetOrdersWithoutCSM({
        accountId: currentWorkspace?.accountId,
        organizationId: currentWorkspace?.organizationId,
        workspaceId: currentWorkspace?.id,
        limit,
        offset,
      }),
  });
};

export const useGetRetroplanningSent = () => {
  const currentWorkspace = useAtomValue(currentWorkspaceAtom);

  return useQuery({
    queryKey: [
      "dashboard",
      "retroplanning",
      "sent",
      currentWorkspace?.accountId,
      currentWorkspace?.organizationId,
      currentWorkspace?.id,
    ],
    enabled: !!currentWorkspace,
    queryFn: () =>
      apiClient.dashboardControllerGetRetroplanningSent({
        accountId: currentWorkspace?.accountId,
        organizationId: currentWorkspace?.organizationId,
        workspaceId: currentWorkspace?.id,
      }),
  });
};

export const useGetDashboardMetrics = () => {
  const currentWorkspace = useAtomValue(currentWorkspaceAtom);

  return useQuery({
    queryKey: [
      "dashboard",
      "metrics",
      currentWorkspace?.accountId,
      currentWorkspace?.organizationId,
      currentWorkspace?.id,
    ],
    enabled: !!currentWorkspace,
    queryFn: () =>
      apiClient.dashboardControllerGetDashboardMetrics({
        accountId: currentWorkspace?.accountId,
        organizationId: currentWorkspace?.organizationId,
        workspaceId: currentWorkspace?.id,
      }),
  });
};
